/**
 * ================================================================================================
 *  ONE PERSON, ONE SCREEN. **Section 6, 2026-10-27.**
 * ================================================================================================
 *
 * Owner, verbatim:
 *
 *   > *"לחיצה על משתמש ברשימה צריכה לפתוח כרטיס עם: הפרטים שהוזנו ומצב המשתמש · התפקידים שלו ·
 *   > החברות שאליהן הוא מורשה ורמת הגישה לכל חברה · המודולים הזמינים לו בהתאם לזכאות הארגון
 *   > ולהרשאותיו · פעולות עריכה, שמירה וחזרה ברורות."*
 *   > *"שינוי הרשאות יתבצע מתוך ההקשר של המשתמש שנבחר, בלי לבחור אותו שוב במסכים שונים."*
 *   > *"רמות „צפייה”, „עדכון” ו„אישור” דורשות הסבר קצר ומעשי, התואם להתנהגות בפועל. רמת גישה
 *   > לחברה אינה מעניקה יכולות שאינן בתפקיד המשתמש."*
 *
 * ── WHAT WAS WRONG: NOT A MISSING SCREEN, A MISSING SUBJECT ─────────────────────────────────────
 *
 * Everything on this card already existed, in three places, each organised around a different
 * subject: the users list knows people, the roles screen knows roles, the access screen knows
 * grants. To change one person's access an administrator had to find that person three times, and
 * nothing anywhere showed what they currently hold. That is what *"בלי לבחור אותו שוב במסכים
 * שונים"* asks for, and it is an information-architecture fault rather than a feature gap.
 *
 * So this screen introduces NO new route and NO new capability. Every read and every write below is
 * one the product already serves, and each is still gated on exactly what it was gated on before —
 * the only thing that changed is that the membership is taken from the address instead of from a
 * picker, so the person stays the subject across all of it.
 *
 * ── THE RULE ABOUT WHAT A LEVEL MEANS, STATED WHERE IT IS CHOSEN ────────────────────────────────
 *
 * *"רמת גישה לחברה אינה מעניקה יכולות שאינן בתפקיד המשתמש."* The two axes multiply, they do not
 * add: the role says what a person may do anywhere, the grant says in which companies. Choosing
 * `אישור` for somebody whose role cannot approve gives them nothing, and the card says so in one
 * line beside the control rather than leaving an administrator to discover it.
 *
 * ── AND WHAT IT STILL CANNOT SHOW ───────────────────────────────────────────────────────────────
 *
 * The modules a person can reach depend on the ORGANISATION's entitlement (`tenant.billing.read`,
 * capability `billing.plan.read`) and on their own role. A session without that capability is told
 * the list could not be read, which is a different fact from an organisation that bought nothing —
 * 09 §5.8, and the distinction this product keeps everywhere else.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { useLocale } from '../i18n/LocaleProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { InfoHint } from '../components/InfoHint';

import { grantOutcomeText } from '../api/grantOutcomeText';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { InfoNote } from '../components/InfoNote';
import { FieldGroup } from '../components/FieldGroup';
import { TabStrip } from '../components/TabStrip';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import {
  accessLevelHe,
  accessLevelText,
  capabilityDescriptionHe,
  membershipStatusHe,
  roleLabel,
} from '../api/adminVocabulary';
import { OFFERED_ACCESS_LEVELS, ACCESS_LEVEL_PERMITS, ACTION_CLASSES } from '../api/contract';
import type { ActionClass, MembershipSummary } from '../api/contract';
import type { TextKey } from '../i18n/catalogue';
import type { AccessLevel, CompanyGrantSummary } from '../api/contract';
import { listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';

/**
 * The four action classes, in the words a reader uses. Keyed by the class the gate names, so a
 * class added to the contract is a compile error here rather than a blank in a sentence.
 */
const ACTION_CLASS_KEY: Readonly<Record<ActionClass, TextKey>> = {
  read: 'action.read',
  write: 'action.write',
  export: 'action.export',
  deliver: 'action.deliver',
};

/**
 * The capability DOMAINS, named. The key is the first segment of a capability code -- the
 * product's own split -- and a domain nobody has named yet falls back to `אחר` rather than to a
 * raw English word in a Hebrew list.
 */
const DOMAIN_KEY: Readonly<Record<string, TextKey>> = {
  companies: 'domain.companies',
  tenant: 'domain.tenant',
  billing: 'domain.billing',
  security: 'domain.security',
  audit: 'domain.audit',
  platform: 'domain.platform',
  user: 'domain.user',
};

export interface TenantUserScreenProps {
  /** From the address. The ONE subject of this screen, never chosen again inside it. */
  readonly membershipUid: string;
}

type Outcome =
  | { readonly kind: 'rolesSaved' }
  | { readonly kind: 'accessSaved'; readonly companyUid: string }
  | { readonly kind: 'accessRevoked'; readonly companyUid: string }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function TenantUserScreen({ membershipUid }: TenantUserScreenProps): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();

  const readMembers = useCallback(() => callOperation('tenant.member.read', {}), []);
  const readRoles = useCallback(() => callOperation('tenant.role.read', {}), []);
  const readGrants = useCallback(() => callOperation('tenant.grant.read', {}), []);
  const readRegister = useCallback(() => callOperation('companies.register.read', {}), []);
  const readSubscription = useCallback(() => callOperation('tenant.billing.read', {}), []);
  /*
   * WHAT BECAME OF THE CHOICES MADE FOR THIS PERSON BEFORE THEY ARRIVED. **2026-10-28.**
   *
   * Owner: *"תוצאת סירוב במסד וביומן אינה מספיקה אם הממשק עדיין מציג הצלחה מלאה."* The
   * redemption re-checks the inviter's authority and records a refusal in the intent row and in the
   * audit trail; neither was on a screen anybody opens.
   */
  const readOutcomes = useCallback(
    () => callOperation('tenant.user.grant_outcome.read', { params: { membership_uid: membershipUid } }),
    [membershipUid],
  );

  const members = useServerData(readMembers);
  const outcomes = useServerData(readOutcomes);
  const roles = useServerData(readRoles);
  const grants = useServerData(readGrants);
  const register = useServerData(readRegister);
  const subscription = useServerData(readSubscription);

  const [chosenRoles, setChosenRoles] = useState<readonly string[] | null>(null);
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /** The company whose level is being changed, and to what. `null` ⇒ nothing is being edited. */
  const [editing, setEditing] = useState<{ companyUid: string; level: AccessLevel } | null>(null);
  const [addingCompanyUid, setAddingCompanyUid] = useState('');
  const [addingLevel, setAddingLevel] = useState<AccessLevel>('read');

  /*
   * ── EDITING A MEMBER'S NAME AND NUMBER. **Owner section 1, 2026-10-29.** ──────────────
   *
   *     *"מנהל מורשה יוכל לנהל משתמשים בהתאם לסמכותו, בלי לעקוף אימות כתובת או
   *     להשתלט על זהות הכניסה שלהם."*
   *
   * TWO FIELDS, AND THE ADDRESS IS NOT ONE OF THEM. It is not disabled here -- it is ABSENT, and
   * the line beside it says where a change of address comes from instead: the person themselves,
   * proving they control the mailbox. A greyed-out address box would say that an administrator is
   * nearly able to do this, which is the opposite of the rule.
   *
   * ── THESE FOUR WERE DECLARED 230 LINES LOWER, AFTER AN EARLY RETURN. **2026-11-03.** ─────────
   *
   * **REAL CRASH ON STAGING:** opening a user card from the list died with `Minified React error
   * #310` at `TenantUserScreen.tsx:379:35`, and the screen showed *"הפעולה לא הושלמה"*.
   *
   * `ctx.surface !== 'TENANT'` returns early at the top of this component. These four `useState`
   * calls sat BELOW it, so the first render -- taken while the session context had not yet
   * resolved to `TENANT` -- ran seven hooks and returned the surface notice, and the render after
   * the context arrived ran eleven. React counts hooks per render and refuses a component whose
   * count moves: *"Rendered more hooks than during the previous render."*
   *
   * NOTHING ABOUT THE FEATURE CHANGED. The declarations moved above the early return, which is
   * where every hook in a component has to be. The guard below still returns the same notice for
   * a platform session; it simply no longer decides how many hooks this component has.
   */
  const [editName, setEditName] = useState<string | null>(null);
  const [editPhone, setEditPhone] = useState<string | null>(null);
  const [savingMember, setSavingMember] = useState(false);
  const [memberSaved, setMemberSaved] = useState<ActionFailure | 'saved' | null>(null);

  /*
   * A MODULE CODE IS NOT A NAME. `tenant.billing.read` answers with the CODES the organisation
   * bought; the names are produced per company by the entitlement read the shell already holds.
   * Where there is no name to be had, the code is shown as a code -- LTR, in a `code` element --
   * rather than dressed up as Hebrew.
   */
  const moduleNameHe = (code: string): string =>
    ctx.modules.find((m) => m.moduleCode === code)?.displayNameHe ?? code;

  const capabilities = ctx.session?.capabilities ?? [];
  const mayAssignRole = capabilities.includes('tenant.role.manage');
  const mayManageAccess = capabilities.includes('companies.assignment.manage');

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('userCard.title')} requires="TENANT" actual={ctx.surface} />;
  }

  const member =
    members.result.state === 'loaded'
      ? members.result.data.members.find((m) => m.membershipUid === membershipUid) ?? null
      : null;
  const roleRows = roles.result.state === 'loaded' ? roles.result.data.roles : [];
  const companyRows = register.result.state === 'loaded' ? register.result.data.companies : [];
  const companyName = (uid: string): string =>
    companyRows.find((c) => c.companyUid === uid)?.displayName ?? t('access.companyUnavailable');
  const theirGrants: readonly CompanyGrantSummary[] =
    grants.result.state === 'loaded'
      ? grants.result.data.grants.filter((g) => g.membershipUid === membershipUid)
      : [];
  const granted = new Set(theirGrants.map((g) => g.companyUid));
  const ungranted = companyRows.filter((c) => !granted.has(c.companyUid));

  /** What is ticked right now: the edit in progress, or what the server says they hold. */
  const ticked = chosenRoles ?? member?.roleCodes ?? [];

  async function saveRoles(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setOutcome(null);
    try {
      await callOperation('tenant.user.role.assign', {
        body: { membershipUid, roleCodes: ticked },
      });
      setChosenRoles(null);
      // RE-READ. The card states what the server holds, never what this screen just sent.
      members.reload();
      setOutcome({ kind: 'rolesSaved' });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  /**
   * CHANGE A LEVEL, OR ADD A COMPANY. One write for both.
   *
   * Owner, section 7: *"אפשר שינוי ברור של הרשאה קיימת, בלי לדרוש הסרה ויצירה מחדש."* The store
   * now updates the row that already exists rather than inserting a second one — which is what
   * makes this a single act instead of a revoke followed by a grant with a hole between them.
   */
  async function grantAccess(companyUid: string, level: AccessLevel): Promise<void> {
    setPending(true);
    setOutcome(null);
    try {
      await callOperation('tenant.grant.create', {
        body: { membershipUid, companyUids: [companyUid], accessLevel: level },
      });
      setEditing(null);
      setAddingCompanyUid('');
      grants.reload();
      setOutcome({ kind: 'accessSaved', companyUid });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  async function revokeAccess(companyUid: string): Promise<void> {
    setPending(true);
    setOutcome(null);
    try {
      await callOperation('tenant.grant.revoke', { body: { membershipUid, companyUid } });
      grants.reload();
      setOutcome({ kind: 'accessRevoked', companyUid });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  const accessColumns: readonly Column<CompanyGrantSummary>[] = [
    {
      key: 'company',
      headerHe: t('access.company'),
      render: (g) => companyName(g.companyUid),
    },
    {
      key: 'level',
      headerHe: t('access.level'),
      /*
       * A DROPDOWN, and it used to be radios. **Owner, 2026-10-28:** *"לא מבין את הגישות,
       * דיברנו על זה, תראה באייקונט איך זה בנוי"*, with the reference beside it: ONE field, named
       * הרשאות, chosen from a list. The same control is on the access screen and the invitation,
       * so the choice looks the same wherever it is made.
       *
       * 09 §3.4 is UNTOUCHED and still absolute. It says *"Company and period are selected in the
       * context bar and nowhere else. No module ever gets its own COMPANY dropdown again."* This
       * dropdown selects neither a company nor a period; `contextSingleSource.test.ts` now holds
       * the rule its authority states, with each permitted control named one by one.
       */
      render: (g) =>
        editing !== null && editing.companyUid === g.companyUid ? (
          <select
            className="field__control"
            name={`level-${g.companyUid}`}
            value={editing.level}
            onChange={(e) =>
              setEditing({ companyUid: g.companyUid, level: e.currentTarget.value as AccessLevel })
            }
          >
            {OFFERED_ACCESS_LEVELS.map((value) => (
              <option key={value} value={value}>
                {accessLevelHe(value)}
              </option>
            ))}
          </select>
        ) : (
          accessLevelHe(g.accessLevel)
        ),
    },
    {
      key: 'actions',
      headerHe: t('userCard.actions'),
      render: (g) =>
        !mayManageAccess ? (
          <span className="gp-muted">{t('userCard.noAccessRight')}</span>
        ) : editing !== null && editing.companyUid === g.companyUid ? (
          <span className="actions-row">
            <button
              type="button"
              className="btn btn--primary"
              disabled={pending}
              onClick={() => void grantAccess(g.companyUid, editing.level)}
            >
              {t('common.save')}
            </button>
            <button type="button" className="btn" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </button>
          </span>
        ) : (
          <span className="actions-row">
            <button
              type="button"
              className="btn"
              onClick={() => setEditing({ companyUid: g.companyUid, level: g.accessLevel })}
            >
              {t('userCard.changeLevel')}
            </button>
            <button
              type="button"
              className="btn"
              disabled={pending}
              onClick={() => void revokeAccess(g.companyUid)}
            >
              {t('access.revoke')}
            </button>
          </span>
        ),
    },
  ];

  /*
   * ── WHAT THIS PERSON CAN ACTUALLY DO, GROUPED BY DOMAIN. **Section 5, 2026-10-28.** ───────────
   *
   * Owner: *"הצג הרשאות בקבוצות לפי מודול או תחום, עם אפשרות לפתוח פירוט ... המשתמש המנהל צריך
   * להבין את התוצאה שלו בלי לקרוא רשימה ארוכה של קודים."*
   *
   * The capabilities are the union of what their roles carry — the same list the roles screen
   * shows per role, read here per PERSON, which is the question an administrator standing on this
   * card is asking. The grouping key is the capability code's own first segment, so it is the
   * product's own domain split and not a second taxonomy invented for a screen.
   */
  const theirCapabilities = [
    ...new Set(
      roleRows.filter((r) => (member?.roleCodes ?? []).includes(r.roleCode)).flatMap((r) => r.capabilities),
    ),
  ].sort();

  const byDomain = new Map<string, readonly string[]>();
  for (const code of theirCapabilities) {
    const domain = code.split('.')[0] ?? code;
    byDomain.set(domain, [...(byDomain.get(domain) ?? []), code]);
  }

  /**
   * One level, in the words of the table the gate reads. **Section 5.**
   *
   * `ACCESS_LEVEL_PERMITS` is mirrored from `packages/contracts/src/enums.ts` and compared to it
   * cell by cell by `contract.mirror.test.ts`. So this sentence is not a claim about the product,
   * it is a reading of the product — including the uncomfortable half: `approve` permits exactly
   * what `write` permits, and the card says so rather than implying a distinction.
   */
  const permitsHe = (level: AccessLevel): string =>
    ACTION_CLASSES.filter((action) => ACCESS_LEVEL_PERMITS[level][action])
      .map((action) => t(ACTION_CLASS_KEY[action]))
      .join(' · ');

  const approveAddsNothing = ACTION_CLASSES.every(
    (action) => ACCESS_LEVEL_PERMITS.approve[action] === ACCESS_LEVEL_PERMITS.write[action],
  );

  // ── THE THREE TABS ────────────────────────────────────────────────────────────────────────────
  const saveMember = async (event: React.FormEvent, current: MembershipSummary): Promise<void> => {
    event.preventDefault();
    setSavingMember(true);
    setMemberSaved(null);
    try {
      await callOperation('tenant.user.update', {
        params: { user_uid: current.userUid },
        /* Exactly the two fields the route serves; the server refuses any other BY NAME. */
        body: { displayName: editName ?? current.displayName, phone: editPhone ?? null },
      });
      members.reload();
      setMemberSaved('saved');
      setEditName(null);
      setEditPhone(null);
    } catch (error) {
      setMemberSaved(classifyActionError(error));
    } finally {
      setSavingMember(false);
    }
  };

  const detailsTab = (): JSX.Element => (
    <>
      {member === null ? null : (
        <FieldGroup titleHe={t('userCard.groupEdit')}>
          <form className="form" onSubmit={(e) => void saveMember(e, member)}>
            <label className="field">
              <span className="field__label">{t('invite.name')}</span>
              <input
                className="field__input"
                name="displayName"
                value={editName ?? member.displayName}
                onChange={(e) => setEditName(e.currentTarget.value)}
                maxLength={128}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">{t('profile.phoneLabel')}</span>
              <input
                className="field__input"
                name="phone"
                dir="ltr"
                inputMode="tel"
                value={editPhone ?? ''}
                onChange={(e) => setEditPhone(e.currentTarget.value)}
                maxLength={32}
              />
              <span className="field__hint">{t('userCard.addressElsewhere')}</span>
            </label>
            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={savingMember}>
                {savingMember ? t('profile.saving') : t('profile.save')}
              </button>
            </div>
          </form>
          {memberSaved === null ? null : memberSaved === 'saved' ? (
            <StateBlock titleHe={t('profile.savedTitle')} bodyHe={t('profile.savedBody')} />
          ) : (
            <ActionFailureNotice failure={memberSaved} />
          )}
        </FieldGroup>
      )}

      <FieldGroup titleHe={t('userCard.groupBasic')} defaultOpen>
        <dl className="fact-list">
          <div className="fact-list__row">
            <dt>{t('invite.name')}</dt>
            <dd>{member?.displayName}</dd>
          </div>
          <div className="fact-list__row">
            <dt>{t('userCard.state')}</dt>
            <dd>{member === null ? null : <StatusChip {...membershipStatusHe(member.status)} />}</dd>
          </div>
          <div className="fact-list__row">
            <dt>{t('userCard.seat')}</dt>
            <dd>{member?.consumesSeat === true ? t('common.yes') : t('common.no')}</dd>
          </div>
        </dl>
      </FieldGroup>

      <FieldGroup titleHe={t('userCard.groupContact')}>
        <dl className="fact-list">
          <div className="fact-list__row">
            <dt>{t('invite.email')}</dt>
            {/* 09 §9.5 A1: bidi is controlled, never inferred. */}
            <dd dir="ltr">{member?.email}</dd>
          </div>
        </dl>
      </FieldGroup>

      {/*
        THE ACCOUNT GROUP CARRIES IDENTIFIERS AND NOTHING ELSE. No password, no token, no recovery
        code: *"אין להציג סיסמאות או סודות בכרטיס המשתמש"*, and there is no field on this screen's
        reads that could carry one.
      */}
      {/*
        NO LEAD ON THIS GROUP. **Owner, 2026-10-28:** the card said *מזהים בלבד. אין בכרטיס סיסמאות,
        אסימונים או קודי שחזור.* It is true and it is HIS OWN RULE — and a sentence announcing that a
        secret is absent is still a sentence nobody needs to read, on a card whose whole content is
        four identifiers they can see. The absence is the control; it is enforced in the service and
        asserted in `tenantUserCard.dom.test.tsx`, not promised in prose.
      */}
      <FieldGroup titleHe={t('userCard.groupAccount')}>
        <dl className="fact-list">
          <div className="fact-list__row">
            <dt>{t('userCard.membershipId')}</dt>
            <dd dir="ltr">
              <code>{membershipUid}</code>
            </dd>
          </div>
        </dl>
      </FieldGroup>
    </>
  );

  /**
   * WHAT WAS CHOSEN FOR THIS PERSON AT INVITATION, AND WHAT HAPPENED TO IT. **2026-10-28.**
   *
   * At the TOP of the permissions tab, before the roles form, because it is the answer to the
   * question somebody opens this tab asking: *why does this colleague not have the role I picked.*
   *
   * REFUSALS FIRST, and applied ones after. Not sorted for tidiness: a refusal is the only row here
   * that needs a decision, and burying it under three successes is how it goes unseen — which is
   * the whole finding. Nothing is filtered out: *"אין להעלים תוצאות שנדחו."*
   *
   * AND IT RENDERS NOTHING AT ALL when there are no decided choices, which is the ordinary case for
   * a person who was never invited through the wizard. An empty block with a heading would put a
   * section on every card to say that nothing happened.
   */
  const grantOutcomesBlock = (): JSX.Element | null => {
    const rows = listOf(outcomes.result, 'outcomes');
    if (rows.length === 0) return null;

    const ordered = [...rows].sort((a, b) => {
      const rank = (outcome: string): number => (grantOutcomeText(outcome).kind === 'refused' ? 0 : 1);
      return rank(a.outcome) - rank(b.outcome);
    });

    /*
     * THE CARD'S OWN GROUP COMPONENT, open by default. `FieldGroup` is what the details tab uses
     * for every collapsible group on this card — the Owner's *"קבוצות פרטים מתקפלות"* — and a
     * hand-rolled `<section>` here would be a second group treatment on one screen. OPEN, because
     * a refusal nobody expands is a refusal nobody sees.
     */
    return (
      <FieldGroup titleHe={t('userCard.outcomesTitle')} defaultOpen>
        <ul className="outcome-list">
          {ordered.map((row) => {
            const text = grantOutcomeText(row.outcome);
            const what =
              row.kind === 'role'
                ? (row.roleNameHe ?? row.roleCode ?? '')
                : `${row.companyName ?? row.companyUid ?? ''}${row.accessLevel === null ? '' : ` \u00b7 ${accessLevelText(row.accessLevel)}`}`;
            return (
              <li key={`${row.kind}-${row.roleCode ?? row.companyUid ?? row.decidedAtUtc}`}>
                <span className="chip">{what}</span>{' '}
                <StatusChip labelHe={text.labelHe} tone={text.kind === 'applied' ? 'ok' : 'warn'} />
              </li>
            );
          })}
        </ul>
      </FieldGroup>
    );
  };

  const permissionsTab = (): JSX.Element => (
    <>
      {grantOutcomesBlock()}
      <ServerRead result={roles.result} whatHe={t('roles.reading')} skeletonRows={2}>
        {(data) =>
          !mayAssignRole ? (
            <>
              <p>
                {(member?.roleCodes.length ?? 0) === 0
                  ? t('userCard.noRoles')
                  : data.roles
                      .filter((r) => (member?.roleCodes ?? []).includes(r.roleCode))
                      .map((r) => roleLabel(r))
                      .join(' · ')}
              </p>
              <p className="gp-muted">{t('userCard.noRoleRight')}</p>
            </>
          ) : (
            <form className="form" onSubmit={(e) => void saveRoles(e)}>
              <fieldset className="field field--choice">
                <legend className="field__label">{t('userCard.roles')}</legend>
                {data.roles.map((role) => (
                  <label key={role.roleCode} className="choice">
                    <input
                      type="checkbox"
                      name="roleCodes"
                      value={role.roleCode}
                      checked={ticked.includes(role.roleCode)}
                      onChange={(e) => {
                        const on = e.currentTarget.checked;
                        setChosenRoles((was) => {
                          const base = was ?? member?.roleCodes ?? [];
                          return on
                            ? [...base, role.roleCode]
                            : base.filter((c) => c !== role.roleCode);
                        });
                      }}
                    />
                    <span>{roleLabel(role)}</span>
                    {/*
                      ⓘ — AND THIS IS THE CASE THE MARK EXISTS FOR. **2026-10-28.**

                      Owner: *"לא מבין מה זה בעלים של הארגון, מנהל הארגון ועובד הארגון."* He cannot
                      know, and nor can anybody: three names, and nothing on the screen said what
                      any of them permits. The office AUTHORS these roles, so a fixed sentence per
                      name would be wrong the first time somebody edits one.

                      What is behind the mark is the role's OWN capability list, as the server
                      returned it with the role — always current, and the same list the gate reads.
                      A role with none says so: an empty set is a real state, and a blank mark
                      would read as a missing answer.
                    */}
                    <InfoHint aboutHe={roleLabel(role)}>
                      {role.capabilities.length === 0 ? (
                        <p>{t('userCard.roleGrantsNothing')}</p>
                      ) : (
                        <ul className="chip-list">
                          {role.capabilities.map((code) => (
                            <li key={code} className="chip">
                              {capabilityDescriptionHe(code)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </InfoHint>
                  </label>
                ))}
              </fieldset>
              <div className="actions-row">
                <button type="submit" className="btn btn--primary" disabled={pending}>
                  {pending ? t('common.working') : t('common.save')}
                </button>
                {chosenRoles === null ? null : (
                  <button type="button" className="btn" onClick={() => setChosenRoles(null)}>
                    {t('common.cancel')}
                  </button>
                )}
              </div>
              {outcome !== null && outcome.kind === 'rolesSaved' ? (
                <p className="field__hint">{t('userCard.rolesSaved')}</p>
              ) : null}
            </form>
          )
        }
      </ServerRead>

      {/*
        ── AND WHAT THE ROLES ADD UP TO. **Section 5.** ──────────────────────────────────────────
        A list of role names does not answer *"what can this person do"*. This does, grouped by
        domain, each group closed until somebody wants it.
      */}
      <h3>{t('userCard.effective')}</h3>
      {theirCapabilities.length === 0 ? (
        <p>{t('userCard.effectiveNone')}</p>
      ) : (
        [...byDomain.entries()].map(([domain, codes]) => (
          <FieldGroup
            key={domain}
            titleHe={t(DOMAIN_KEY[domain] ?? 'userCard.domainOther')}
            leadHe={`${String(codes.length)} ${t('userCard.capabilityCount')}`}
          >
            <ul className="act-list">
              {codes.map((code) => (
                <li key={code}>
                  {/*
                    THE PRODUCT'S OWN WORDS FOR THE CODE, and the CODE itself when the vocabulary
                    has never seen it — `capabilityDescriptionHe` answers null rather than
                    inventing, and a name nobody wrote is better shown than guessed.
                  */}
                  {capabilityDescriptionHe(code) ?? <code dir="ltr">{code}</code>}
                </li>
              ))}
            </ul>
          </FieldGroup>
        ))
      )}
    </>
  );

  const companiesTab = (): JSX.Element => (
    <>
      <ServerRead result={grants.result} whatHe={t('access.reading')} skeletonRows={3}>
        {() =>
          theirGrants.length === 0 ? (
            <p>{t('userCard.noCompanies')}</p>
          ) : (
            <DataTable<CompanyGrantSummary>
              captionHe={t('userCard.access')}
              columns={accessColumns}
              rows={theirGrants}
              rowKey={(g) => g.companyUid}
              emptyStateHe={t('userCard.noCompanies')}
            />
          )
        }
      </ServerRead>

      {/*
        WHAT A LEVEL ACTUALLY ALLOWS, READ FROM THE GATE'S OWN TABLE — not a sentence about it.
        Including the finding: today `אישור` and `עדכון` are the same permission under two names.
      */}
      <InfoNote summaryHe={t('userCard.levelsHelp')}>
        {OFFERED_ACCESS_LEVELS.map((level) => (
          <p key={level}>
            <strong>{accessLevelHe(level)}</strong>
            {` — ${permitsHe(level)}`}
          </p>
        ))}
        {approveAddsNothing ? <p>{t('userCard.approveSameAsWrite')}</p> : null}
        <p>{t('userCard.twoAxes')}</p>
      </InfoNote>

      {!mayManageAccess || ungranted.length === 0 ? null : (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (addingCompanyUid !== '') void grantAccess(addingCompanyUid, addingLevel);
          }}
        >
          <fieldset className="field field--choice">
            <legend className="field__label">{t('userCard.addCompany')}</legend>
            {ungranted.map((company) => (
              <label key={company.companyUid} className="choice">
                <input
                  type="radio"
                  name="companyUid"
                  value={company.companyUid}
                  checked={addingCompanyUid === company.companyUid}
                  onChange={() => setAddingCompanyUid(company.companyUid)}
                />
                <span>{company.displayName}</span>
              </label>
            ))}
          </fieldset>
          <fieldset className="field field--choice">
            <legend className="field__label">{t('access.level')}</legend>
            {OFFERED_ACCESS_LEVELS.map((value) => (
              <label key={value} className="choice">
                <input
                  type="radio"
                  name="newAccessLevel"
                  value={value}
                  checked={addingLevel === value}
                  onChange={() => setAddingLevel(value)}
                />
                <span>{accessLevelHe(value)}</span>
              </label>
            ))}
          </fieldset>
          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={pending || addingCompanyUid === ''}
            >
              {pending ? t('common.working') : t('userCard.grant')}
            </button>
          </div>
        </form>
      )}
    </>
  );

  const modulesTab = (): JSX.Element => (
    <ServerRead result={subscription.result} whatHe={t('userCard.readingModules')} skeletonRows={2}>
      {(data) =>
        data.licensedModules.length === 0 ? (
          <p>{t('userCard.noModules')}</p>
        ) : (
          <>
            <ul className="act-list">
              {data.licensedModules.map((code: string) => (
                <li key={code}>{moduleNameHe(code)}</li>
              ))}
            </ul>
            {/*
              THE THREE AXES, IN ORDER, ONCE. *"יש לשמר את ההפרדה בין: זכאות הארגון למודול →
              יכולות התפקיד → גישה לחברה."* A module the organisation never bought opens for
              nobody, whatever their role says.
            */}
          </>
        )
      }
    </ServerRead>
  );

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      screenId="tenant-user"
      /*
        ── THE PERSON'S NAME APPEARS ONCE ON THIS SCREEN. **Section 4/1, 2026-10-28.** ───────────

        The shell title carried the member's name and their address, and the card head below it
        carried the same name and the same address again -- the duplicate frame the Owner struck
        off the home page in section 1, repeated here one screen over. The head is the one he asked
        to keep: *"בראש הכרטיס נשארים שם המשתמש ופרטי הזיהוי הרלוונטיים"*, and it also carries the
        state chip, which the title cannot.

        So the SHELL now names the SCREEN -- כרטיס משתמש -- and the identity lives in the head,
        once. No `leadHe` either: the address was the duplicated line.
      */
      titleHe={t('userCard.title')}
      backToParent={{ labelHe: t('users.title'), href: routes.tenantUsers }}
    >
      {outcome !== null && outcome.kind === 'failed' ? (
        <ActionFailureNotice
          failure={outcome.failure}
          /* Section 3's rule: the factor is given here, and the write is NOT replayed by itself. */
          onStepUpSatisfied={() => setOutcome(null)}
          continueLabelHe={t('common.save')}
        />
      ) : null}

      <ServerRead result={members.result} whatHe={t('users.reading')} skeletonRows={3}>
        {() =>
          member === null ? (
            <StateBlock
              titleHe={t('userCard.missingTitle')}
              bodyHe={t('userCard.missingBody')}
              tone="unavailable"
              nextStep={
                <a className="btn btn--nav" href={routes.tenantUsers}>
                  {t('users.title')}
                </a>
              }
            />
          ) : (
            <>
              {/*
                ── THE HEAD OF THE CARD, FIXED. **Section 4, 2026-10-28.** ────────────────────────
                *"בראש הכרטיס נשארים שם המשתמש ופרטי הזיהוי הרלוונטיים."* Who this is, and what
                state they are in, stays put while the tabs below change — a warning that appears
                only when you happen to open the right tab is a warning the product cannot claim it
                gave. The same rule `TabStrip`'s own header states for the company card.
              */}
              <section className="gp-panel record-head">
                <div className="record-head__identity">
                  <h2>{member.displayName}</h2>
                  <span className="gp-muted" dir="ltr">
                    {member.email}
                  </span>
                </div>
                <div className="record-head__state">
                  <StatusChip {...membershipStatusHe(member.status)} />
                </div>
              </section>

              <TabStrip
                name="user"
                ariaLabelHe={t('userCard.tabsAria')}
                tabs={[
                  { id: 'details', labelHe: t('userCard.tabDetails'), render: detailsTab },
                  {
                    id: 'permissions',
                    labelHe: t('userCard.tabPermissions'),
                    /* A person with no role can do nothing: that is worth a mark on the tab. */
                    ...(member.roleCodes.length === 0 ? { outstanding: true as const } : {}),
                    render: permissionsTab,
                  },
                  { id: 'companies', labelHe: t('userCard.tabCompanies'), render: companiesTab },
                  { id: 'modules', labelHe: t('userCard.modules'), render: modulesTab },
                ]}
              />
            </>
          )
        }
      </ServerRead>
    </AppShell>
  );
}
